import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    FC,
} from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

// Context setup
type SnackbarContextType = {
    showSnackbar: (
        message: string,
        severity?: AlertColor,
        autoHideDuration?: number
    ) => void;
};

const SnackbarContext = createContext<SnackbarContextType | undefined>(
    undefined
);

// Hook to use snackbar
export const useSnackbar = () => {
    const context = useContext(SnackbarContext);
    if (!context) {
        throw new Error('useSnackbar must be used within a SnackbarProvider');
    }
    return context;
};

// Provider Component
const SnackbarProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('info');
    const [snackbarDuration, setSnackbarDuration] = useState(6000);

    const showSnackbar = (
        message: string,
        severity: AlertColor = 'info',
        autoHideDuration?: number
    ) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarDuration(autoHideDuration ?? 6000);
        setSnackbarOpen(true);
    };

    const handleClose = () => {
        setSnackbarOpen(false);
    };

    return (
        <SnackbarContext.Provider value={{ showSnackbar }}>
            {children}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={snackbarDuration}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleClose}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </SnackbarContext.Provider>
    );
};

export default SnackbarProvider;
